/**
 * @file BackupInstallCommandBox.tsx
 * @description Показ одноразовой команды установки агента (phase19.26 шаг 7.1):
 * команда + «Скопировать» + обратный отсчёт срока действия.
 *
 * Время «сейчас» передаёт родитель — он же по истечении срока убирает команду
 * из state (`BackupInstallSection`).
 */

import { Copy } from "lucide-react";

import { Button } from "../../ui/button";
import { useNotify } from "../../ui/NotifyProvider";
import type { BackupInstallCommand } from "../../../lib/api-backups";
import { formatTimeLeft, installMsLeft } from "./backup-install";

interface Props {
  install: BackupInstallCommand;
  /** Текущее время, мс — тикает раз в секунду. */
  now: number;
}

export function BackupInstallCommandBox({ install, now }: Props) {
  const notify = useNotify();
  const until = new Date(install.expiresAt).toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
  });

  async function copy() {
    try {
      await navigator.clipboard.writeText(install.command);
      notify.success("Команда скопирована");
    } catch {
      notify.error("Не удалось скопировать — выделите команду вручную");
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex gap-2">
        <pre className="min-w-0 flex-1 overflow-x-auto whitespace-pre-wrap break-all rounded-md bg-input p-3 font-mono text-xs select-all">
          {install.command}
        </pre>
        <Button
          variant="outline"
          size="icon"
          className="shrink-0"
          onClick={() => void copy()}
          aria-label="Скопировать команду"
        >
          <Copy className="size-4" />
        </Button>
      </div>
      <span className="text-xs text-on-surface-variant tabular-nums" aria-live="off">
        Действует ещё {formatTimeLeft(installMsLeft(install.expiresAt, now))} (до {until}),
        срабатывает один раз. Не пересылайте её — в ней ключ доступа агента.
      </span>
    </div>
  );
}
